import React from 'react';
import clsx from 'clsx';
import {
  Checkbox, Form, Input, Modal,
} from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { firstResponseError, pruneServerErrors } from '@/utils/errors';
import { globalFieldLengths } from '@/constants/fields';
import { states } from '@/constants/lifecycle';
import Actions from '@/components/buttons/Actions';
import DialogWrapper from '@/components/dialogs/DialogWrapper';
import { findMatch } from '@/utils/resultsHelper';
import AssignDataToDictionaryTable from './AssignDataToDictionaryTable';
import Selectors from '../hooks/selectors';
import Events from '../hooks/events';
import DatasetEvents from '../../hooks/events';

const AssignDataToDictionaryDialog = ({ theme }) => {
  const intl = useIntl();
  const state = Selectors.useAssignDataToDictionaryDialog();
  const convertDictionary = DatasetEvents.useConvertToDictionary(state);
  const createAndConvert = DatasetEvents.useCreateAndConvertToDictionary(state);
  const hide = Events.useHideConvertToDatasetDialog();
  const updateProperties = Events.useUpdateActiveDictionaryAttachmentProperties();
  const saveErrors = [...state.saveDictionaryErrors, ...state.attachmentErrors];

  const filteredDictionaries = state.dictionaries.filter(
    ({ name }) => name.toLowerCase().includes(state.filterValue.toLocaleLowerCase()),
  );

  const dictionaryMatch = state.dictionaries.find(
    ({ name }) => name.toLowerCase() === state.filterValue.toLocaleLowerCase(),
  );

  const filterDictionaries = ({ target: { value: filter } }) => updateProperties({ properties: { filter } });

  const handleDismiss = () => updateProperties({ properties: { saveErrors: pruneServerErrors(saveErrors) } });

  const setDeleteSource = () => updateProperties({ properties: { deleteSource: !state.deleteSource } });

  const onCreate = () => {
    const {
      attachmentId, filterValue: name, dictionaryFromDatafile: { fields }, deleteSource,
    } = state;
    createAndConvert({
      attachmentId,
      dictionaryProps: { dictionaries: [{ name, fields }] },
      convertProps: { deleteSource },
    });
  };

  const onUpdate = () => {
    const { code: dictionaryCode, name: dictionaryName } = findMatch(state.dictionaries, state.filterValue, 'name');
    const properties = { dictionaryCode, deleteSource: state.deleteSource, dictionaryName };
    convertDictionary({ attachmentId: state.attachmentId, properties, isNew: false });
  };

  const placeHolderText = () => {
    if (filteredDictionaries.length === 0) {
      return 'dataset.convert.type.create';
    }
    if (filteredDictionaries.length === 1) {
      return 'dataset.convert.click.dictionary';
    }
    return 'dataset.convert.dictionary.filter';
  };

  const createUpdateButtonMinRequirements = state.filterValue.length > 0
    && state.saveDictionaryStatus !== (states.PENDING || states.SUCCESS)
    && state.attachmentStatus === states.SUCCESS;

  return (
    <DialogWrapper
      className={clsx('aridhia-assign-data-to-dictionary-dialog', !(state.dictionaries.length <= 1) && 'h-400')}
      error={firstResponseError(saveErrors)}
      icon="arrow alternate circle right"
      onDismiss={handleDismiss}
      theme={theme}
      title={intl.formatMessage({ id: 'dataset.convert.title' })}
    >
      <Modal.Content scrolling className={clsx('pt-10', !(state.dictionaries.length <= 1) && 'h-100p')}>
        <Form
          autoComplete="off"
          className="h-100p flex flex-column min-h-160"
          onSubmit={dictionaryMatch ? onUpdate : onCreate}
        >
          <div className="mt-0 mb-0">
            <Input
              className="mt-5 mb-10 focus"
              icon="filter"
              iconPosition="left"
              fluid
              placeholder={intl.formatMessage({ id: placeHolderText() })}
              onChange={filterDictionaries}
              value={state.filterValue}
              maxLength={globalFieldLengths.code}
              autoFocus
              loading={state.attachmentStatus === states.PENDING}
            />
          </div>
          {(filteredDictionaries.length > 0) && (
            <div className="mt-0 mb-40">
              <AssignDataToDictionaryTable dictionaries={filteredDictionaries} filterValue={state.filterValue} />
            </div>
          )}
        </Form>
      </Modal.Content>
      <Actions
        minimumDataEntryMet={createUpdateButtonMinRequirements}
        onClose={hide}
        onSave={dictionaryMatch ? onUpdate : onCreate}
        saveMessageId={dictionaryMatch ? 'dataset.convert.update' : 'dataset.convert.create'}
      />
      {state.canDelete && (
        <Checkbox
          className="flex toggle--red labelRed assign-data-toggle"
          toggle
          label={intl.formatMessage({ id: 'dataset.convert.delete.on.success' })}
          onClick={setDeleteSource}
        />
      )}
    </DialogWrapper>
  );
};

export default AssignDataToDictionaryDialog;
